import React, { useState } from "react";
import { Icon } from "../components/tailwind";
import { FileDrop } from "../components/FileDrop";
import { Text } from "../components/Text";
import { UploadSettings, UploadSettingsData } from "./UploadSettings";
import { UploadButton } from "./UploadButton";

type Props = {
  token?: string;
  customerSlug?: string;
};

const uploadInstructionsTooltip =
  "• Supported file types are CSV and Excel\n" +
  "• Each row should represent a product\n" +
  "• Only the first tab will be processed\n" +
  "• Key Mappings are applied to map headers to Textual fields from the selected Namespace";

export const ImportDocumentsFromFile: React.FC<Props> = ({ customerSlug }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [settings, setSettings] = useState<UploadSettingsData | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (file: File | null): void => {
    setSelectedFile(file);
  };

  const ALLOWED_EXTENSIONS =
    selectedFile &&
    (selectedFile.name.endsWith(".csv") ||
      selectedFile.name.endsWith(".xlsx") ||
      selectedFile.name.endsWith(".xls"));

  const handleSubmitStart = (): void => {
    setSubmitting(true);
  };

  const handleSubmitError = (error: string): void => {
    alert(error);
    setSubmitting(false);
  };

  // Construct the upload URL
  const getUploadUrl = (): string => {
    // Build the URL using the customer slug
    if (customerSlug) {
      return `/d/${customerSlug}/wizard/upload/`;
    }
    // Fallback: use current URL
    return window.location.pathname;
  };

  const canSubmit =
    selectedFile && ALLOWED_EXTENSIONS && settings && !submitting;

  return (
    <div className="tw-w-full tw-space-y-8">
      {/* File Upload Section */}
      <div className="tw-relative">
        <h2 className="tw-text-xl tw-font-semibold tw-mb-4 tw-text-center">
          Select File
        </h2>
        <div className="tw-relative tw-w-full tw-max-w-md tw-mx-auto tw-flex tw-items-center tw-justify-center">
          <FileDrop
            selectedFile={selectedFile}
            isInvalid={!ALLOWED_EXTENSIONS}
            onChange={handleFileChange}
            contentUnselected={<Text>Supported file types: CSV and Excel</Text>}
          />
          <span
            className="tw-absolute tw-top-0 -tw-right-[40px] tw-cursor-help tw-z-10"
            data-balloon={uploadInstructionsTooltip}
            data-balloon-pos="left"
            data-balloon-length="xlarge"
          >
            <Icon
              name="help_outline"
              className="tw-text-2xl tw-text-gray-600 hover:tw-text-gray-800"
            />
          </span>
        </div>
      </div>

      {/* Upload Settings Section */}
      <div>
        <h2 className="tw-text-xl tw-font-semibold tw-mb-4 tw-text-center">
          Upload Settings
        </h2>
        <UploadSettings customerSlug={customerSlug} onChange={setSettings} />
      </div>

      {settings && (
        <div className="tw-flex tw-justify-center tw-mt-6">
          <UploadButton
            disabled={!canSubmit}
            submitting={submitting}
            readyText="Start Upload"
            submittingText="Uploading..."
            actionUrl={getUploadUrl()}
            settings={settings}
            file={selectedFile}
            onSubmitStart={handleSubmitStart}
            onSubmitError={handleSubmitError}
          />
        </div>
      )}
    </div>
  );
};
